import { Link, useLocation } from "@tanstack/react-router";
import { useContext, useState } from "react";
import { BrandButton } from "@/components/brand/brand-button";
import { Logo } from "@/components/brand/logo";
import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import { useSession } from "../hooks/auth-hooks";
import { DashboardContext } from "./dashboard/DashboardContext";
import { signOutAndNavigate } from "./header/shared";
import { DashboardHeaderNav } from "./header/DashboardHeaderNav";
import { ImpersonationBanner } from "./header/ImpersonationBanner";
import { MobileMenuButton } from "./header/MobileMenuButton";
import { PublicDesktopNav } from "./header/PublicDesktopNav";
import { PublicMobileNav } from "./header/PublicMobileNav";
import { UserMenu } from "./header/UserMenu";
import { useMarketingNavItems } from "./header/useMarketingNavItems";
import { WorkspaceSwitcher } from "./header/WorkspaceSwitcher";

const headerStyle = {
	boxShadow: "rgba(255,255,255,0.06) 0px -1px 0px 0px inset",
	backgroundColor: "rgba(10,10,10,0.85)",
};

// Smart Header wrapper that picks the right header based on auth state
export function Header() {
	const { session, user, isPending } = useSession();

	if (isPending) {
		return (
			<header
				className="sticky top-0 z-50 backdrop-blur-sm"
				style={headerStyle}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<div className="flex h-14 items-center justify-between">
						<Logo size="sm" />
						<div className="flex items-center gap-2">
							<AnimatedThemeToggler className="size-9 rounded-full" />
							<div
								className="size-8 animate-pulse rounded-full"
								style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
							/>
						</div>
					</div>
				</div>
			</header>
		);
	}

	if (session && user) {
		return <LoggedInHeader user={user} session={session} />;
	}

	return <LoggedOutHeader />;
}

// Header for authenticated users
interface LoggedInHeaderProps {
	user: {
		id: string;
		name?: string | null;
		email?: string;
		image?: string | null;
		role?: string | null;
	};
	session: { impersonatedBy?: string | null };
}

export function LoggedInHeader({ user, session }: LoggedInHeaderProps) {
	const location = useLocation();
	const isImpersonating = !!session?.impersonatedBy;
	const [isSigningOut, setIsSigningOut] = useState(false);

	const dashboardCtx = useContext(DashboardContext);

	const showDashboardNav =
		location.pathname.startsWith("/dashboard") && !!dashboardCtx;
	const formattedBalance = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	}).format((dashboardCtx?.balanceCents ?? 0) / 100);

	const handleSignOut = async () => {
		if (isSigningOut) return;
		setIsSigningOut(true);

		try {
			await signOutAndNavigate(location.pathname);
		} catch (error) {
			console.error("Sign out failed:", error);
			setIsSigningOut(false);
		}
	};

	return (
		<header
			className="sticky top-0 z-50 backdrop-blur-sm"
			style={headerStyle}
		>
			<ImpersonationBanner isImpersonating={isImpersonating} user={user} />

			<div className="px-4 sm:px-6">
				<div className="flex h-14 items-center justify-between">
					<div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
						{!showDashboardNav && <Logo size="sm" className="shrink-0 hidden lg:flex" />}

						{showDashboardNav && dashboardCtx && (
							<>
								<WorkspaceSwitcher />

								<div
									className="hidden lg:block w-px h-5"
									style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
								/>

								<DashboardHeaderNav pathname={location.pathname} />
							</>
						)}
					</div>

					<div className="flex items-center gap-1.5">
						{showDashboardNav && dashboardCtx ? (
							<Link
								to="/dashboard/billing"
								search={{ checkout: undefined }}
								aria-label={`Account balance: ${dashboardCtx.isBalanceLoading ? "loading" : formattedBalance}. Go to billing.`}
								className="hidden lg:inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-[6px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{
									boxShadow: "rgba(255,255,255,0.08) 0px 0px 0px 1px",
									backgroundColor: "rgba(255,255,255,0.03)",
									color: "#fafafa",
								}}
								onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.06)"; }}
								onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.03)"; }}
								onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.06)"; }}
								onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.03)"; }}
							>
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>account_balance_wallet</span>
								<span aria-hidden="true">
									{dashboardCtx.isBalanceLoading ? "Loading..." : formattedBalance}
								</span>
							</Link>
						) : null}

						<AnimatedThemeToggler className="size-8 rounded-full" />

						<UserMenu
							user={user}
							isImpersonating={isImpersonating}
							isSigningOut={isSigningOut}
							onSignOut={() => {
								void handleSignOut();
							}}
						/>
					</div>
				</div>
			</div>
		</header>
	);
}

// Header for non-authenticated users
export function LoggedOutHeader() {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const { forWhoItems, featuresItems } = useMarketingNavItems();

	return (
		<header
			className="sticky top-0 z-50 backdrop-blur-sm"
			style={headerStyle}
		>
			<div className="mx-auto flex h-14 max-w-7xl items-center px-4 sm:px-6">
				<Logo size="sm" />

				<PublicDesktopNav
					forWhoItems={forWhoItems}
					featuresItems={featuresItems}
				/>

				<div className="flex items-center gap-3 ml-auto">
					<BrandButton
						variant="outline"
						asChild
						className="hidden h-9 px-4 text-sm sm:flex"
					>
						<Link to="/login">Log in</Link>
					</BrandButton>

					<BrandButton asChild className="hidden h-9 px-4 text-sm sm:flex">
						<Link to="/signup">Get started</Link>
					</BrandButton>

					<AnimatedThemeToggler className="size-8 rounded-full" />

					<MobileMenuButton
						isOpen={mobileMenuOpen}
						onToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
					/>
				</div>
			</div>

			<PublicMobileNav
				variant="logged-out"
				isOpen={mobileMenuOpen}
				forWhoItems={forWhoItems}
				featuresItems={featuresItems}
				onClose={() => setMobileMenuOpen(false)}
			/>
		</header>
	);
}

// Smart PublicHeader for marketing pages
export function PublicHeader() {
	const { session, user, isPending } = useSession();

	if (isPending || !session || !user) {
		return <PublicLoggedOutHeader />;
	}

	return <PublicLoggedInHeader />;
}

function PublicHeaderShell({ children }: { children: React.ReactNode }) {
	return (
		<header
			className="sticky top-0 z-50 backdrop-blur-sm"
			style={headerStyle}
		>
			<div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6">
				<div className="flex items-center gap-3">
					<Logo size="sm" />
				</div>
				<div className="flex items-center gap-4">
					<AnimatedThemeToggler className="size-8 rounded-full" />
					{children}
				</div>
			</div>
		</header>
	);
}

function PublicLoggedOutHeader() {
	return (
		<PublicHeaderShell>
			<BrandButton
				variant="outline"
				asChild
				className="hidden h-9 px-4 text-sm sm:inline-flex"
			>
				<Link to="/login">Log in</Link>
			</BrandButton>
			<BrandButton asChild className="h-9 px-4 text-sm">
				<Link to="/signup">Get started</Link>
			</BrandButton>
		</PublicHeaderShell>
	);
}

function PublicLoggedInHeader() {
	return (
		<PublicHeaderShell>
			<BrandButton asChild className="h-9 px-4 text-sm">
				<Link to="/dashboard">Dashboard</Link>
			</BrandButton>
		</PublicHeaderShell>
	);
}
